/* External */
import { Fragment, useMemo } from 'react';
import { useIntl } from 'react-intl';
import capitalize from 'lodash-es/capitalize';

/* Ott-common */
import { EntityName } from '@dtvgo/backend-service/normalize';
import { VrioContentType } from '@dtvgo/constants';

/* Components */
import Rating from '../components/Rating';

/* Utils */
import { formatDuration, formatStartAndEndTime, isWithinIntervalDate } from './dateUtils';
import {
  SPACE,
  DEFAULT_SEPARATOR,
  DOT_SEPARATOR,
  EpgQualifier,
  LANDER_ACTION_TEXT_SET,
  SPORT_PAGE_SET,
} from './constants';
import { isTapppPanelEnabledForUser } from './profiles';

export function joinArrayWithSeparator(arr, separator = DEFAULT_SEPARATOR) {
  return arr?.filter(Boolean)?.join(separator) || '';
}

export function joinArrayWithSpace(arr, numberOfSpaces = 2) {
  const spaces = Array.from({ length: numberOfSpaces }).fill(SPACE);
  return arr?.filter(Boolean)?.map((item, index) => (
    // eslint-disable-next-line react/no-array-index-key -- there is no a better key
    <Fragment key={index}>
      {index > 0 && spaces}
      {item}
    </Fragment>
  )) || '';
}

export function isShowSchema(schema) {
  return schema === EntityName.SHOW;
}

export function isShowType(type) {
  return type === VrioContentType.SHOW;
}

export function isShow(content) {
  return isShowSchema(content?.schema) || isShowType(content?.type);
}

export function isContent(entityInstance) {
  return entityInstance?.schema === EntityName.CONTENT;
}

export function isSchedule(entityInstance) {
  return entityInstance?.schema === EntityName.SCHEDULE;
}

export function isChannelSchema(schema) {
  return schema === EntityName.CHANNEL;
}

export function isChannelType(type) {
  return type === VrioContentType.CHANNEL;
}

export function isChannel(content) {
  return isChannelSchema(content?.schema) || isChannelType(content?.type);
}

export function isEpisodeType(type) {
  return type === VrioContentType.EPISODE;
}

export function isEpisode(content) {
  return isEpisodeType(content?.type);
}

export function isMovieType(type) {
  return type === VrioContentType.MOVIE;
}

export function isMovie(content) {
  return isMovieType(content?.type);
}

export function isSportsType(type) {
  return type === VrioContentType.SPORT;
}

export function isSports(content) {
  const { episode, title, type } = content || {};
  return isSportsType(type)
    || (episode
      && isEpisodeType(type)
      && ((episode?.number ?? 0) <= 0 || (episode?.seasonNumber ?? 0) <= 0)
      && title !== episode?.showName);
}

export function isEventType(type) {
  return type === VrioContentType.EVENT;
}

export function isEvent(content) {
  return isEventType(content?.type);
}

export function isReplay(schedule) {
  return !!schedule?.qualifiers?.includes(EpgQualifier.REPEAT);
}

export function getRating(content) {
  return content?.rating && <Rating rating={content?.rating} />;
}

export function getReleaseYear(content) {
  return content?.releaseYear;
}

/* Pure core of useTitle (no hook, no intl provider). The react-intl
   `formatMessage` is optional so a non-React caller (the lego player shell,
   which builds the loading screen metadata outside the Solid tree) can reuse
   the same title resolution — including the episode show-name preference and
   the replay prefix — instead of re-implementing it. */
export function getTitle(content, schedule, options = {}) {
  const { formatMessage, invertTitle = false } = options;
  let baseTitle = content?.title || schedule?.title;

  if (isEpisode(content) && content?.episode?.number) {
    baseTitle = invertTitle
      ? baseTitle || content?.episode?.showName
      : content?.episode?.showName || baseTitle;
  }

  if (isReplay(schedule)) {
    return `${formatMessage ? formatMessage({ id: 'common.replay' }) : ''}: ${baseTitle}`;
  }

  return baseTitle;
}

export function useTitle(content, schedule, invertTitle = false) {
  const { formatMessage } = useIntl();
  return getTitle(content, schedule, { formatMessage, invertTitle });
}

/* Pure core of useFullEpisodeNumber — `formatMessage` optional, same contract
   as getTitle's option (kept as a bare param: only one of its two options is
   ever needed). */
export function getFullEpisodeNumber(content, formatMessage) {
  if (!isEpisode(content)) return '';

  const seasonFirstChar = formatMessage ? formatMessage({
    id: 'common.seasonFirstChar',
  }) : 'T';
  const episodeFirstChar = formatMessage ? formatMessage({
    id: 'common.episodeFirstChar',
  }) : 'E';

  const { seasonNumber, number } = content?.episode || {};

  return [
    seasonNumber ? `${seasonFirstChar}${seasonNumber}` : '',
    number ? `${episodeFirstChar}${number}` : '',
  ].filter(Boolean).join(' ');
}

export function useFullEpisodeNumber(content) {
  const { formatMessage } = useIntl();
  return useMemo(() => getFullEpisodeNumber(content, formatMessage), [content, formatMessage]);
}

/* Pure core of useFullEpisodeName — see getTitle above. */
export function getFullEpisodeName(content, options = {}) {
  const { formatMessage, invertTitle = false, separator = DOT_SEPARATOR } = options;
  const episodeName = invertTitle
    ? content?.episode?.showName || content?.title
    : content?.title;
  const fullEpisodeNumber = getFullEpisodeNumber(content, formatMessage);

  if (!isEpisode(content)) return '';

  if (!content?.episode
    || ((content?.episode?.number ?? 0) <= 0 && content.title !== content?.episode?.showName)) {
    return joinArrayWithSeparator(
      [content?.episode?.showName, joinArrayWithSeparator((content?.genres ?? [])?.slice(0, 3))],
    );
  }

  if (content.title === content?.episode?.showName) {
    return joinArrayWithSeparator((content?.genres ?? [])?.slice(0, 3));
  }

  return joinArrayWithSeparator([fullEpisodeNumber, episodeName], separator);
}

export function useFullEpisodeName(content, invertTitle = false, separator = DOT_SEPARATOR) {
  const { formatMessage } = useIntl();
  return useMemo(
    () => getFullEpisodeName(content, { formatMessage, invertTitle, separator }),
    [content, formatMessage, invertTitle, separator],
  );
}

export function getFirstGenre(content) {
  return capitalize((Array.isArray(content?.genres) ? content?.genres[0] : content?.genres?.get?.(0)) || '');
}

function getGenresCapitalized(content) {
  let genresCapitalized;

  if (content?.genres) {
    genresCapitalized = (content?.genres ?? [])?.slice(0, 3)?.map((genre) => {
      if (typeof genre === 'object') return capitalize(genre.name);
      return capitalize(genre);
    });
  }
  if (content?.genre) {
    genresCapitalized = content?.genre?.map((genre) => {
      if (typeof genre === 'object') return capitalize(genre.name);
      return capitalize(genre);
    });
  }

  return genresCapitalized;
}

export function getAllGenres(content, separator = ' • ') {
  return joinArrayWithSeparator(
    getGenresCapitalized(content),
    separator,
  );
}

export function useAllGenres(content, separator = ' • ') {
  return useMemo(() => getAllGenres(content, separator), [content, separator]);
}

export function getFormattedDuration(content) {
  if (!content?.duration) return '';

  return formatDuration(content.duration);
}

export function getSchedule(schedule, timeZone) {
  return formatStartAndEndTime(schedule?.startTime, schedule?.endTime, undefined, timeZone);
}

export function isPlayingNow(schedule, currentTime = new Date()) {
  if (!schedule?.startTime || !schedule.endTime) {
    return false;
  }
  return isWithinIntervalDate(schedule?.startTime, schedule?.endTime, currentTime);
}

export function getWebAction(collection) {
  return collection?.actions?.find(({ type }) => type === 'web');
}

export function getMatchingCollection(collections, target, query) {
  return collections?.find((collection) => {
    const action = getWebAction(collection);
    if (action?.target !== target) return false;

    if (SPORT_PAGE_SET.has(action?.text)) {
      return Object.entries(query).every(([key, value]) => collection.sportIds?.[key] === value);
    }
    return true;
  });
}

export function isLanderCollection(collection) {
  const webAction = getWebAction(collection);
  return LANDER_ACTION_TEXT_SET.has(webAction?.text);
}

export function getRatingWarnings(content) {
  const ratingWarning = content?.ratingWarning?.size > 0 ? ` • ${content?.ratingWarning?.join(', ')}` : '';
  return `${content?.ratingDescription}${ratingWarning}`;
}

export function isSelfClassified(content) {
  return content !== undefined && content?.rating?.charAt(0) === 'A';
}

export function isInteractivityAllowed(content, country, userId) {
  return isTapppPanelEnabledForUser(country, userId) && content?.interactivities?.bets;
}
